import { h } from '../lib/dom.js';
import { getState, fmtNum, fmtReporting, toReporting, totalUSDFor, totalReportingAll, CONFIG } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  openOpenAccount, setAccountSort, goTransfer, goActivityFromHome,
  startHomeEdit, setHomeEditDraft, confirmHomeEdit, cancelHomeEdit,
  toggleBalanceInfo, toggleActivityInfo
} from '../actions.js';

const ACCOUNT_SORTS = {
  'name-asc': (a, b) => a.name.localeCompare(b.name),
  'name-desc': (a, b) => b.name.localeCompare(a.name),
  'balance-desc': (a, b) => totalUSDFor(b) - totalUSDFor(a),
  'balance-asc': (a, b) => totalUSDFor(a) - totalUSDFor(b)
};

function sortIcons(s) {
  const nameIcon = s.accountSort === 'name-desc' ? 'arrowDownZA' : 'arrowDownAZ';
  const balanceIcon = s.accountSort === 'balance-asc' ? 'arrowDown01' : 'arrowDown10';
  const nameActive = s.accountSort.startsWith('name-');
  const balanceActive = s.accountSort.startsWith('balance-');
  return h('div', { class: 'flex-gap-sm' }, [
    h('button', {
      class: 'icon-btn', 'aria-label': 'Sort by name',
      onclick: () => setAccountSort('name')
    }, h('span', { html: iconMarkup(nameIcon, { size: 16, color: nameActive ? '#4D4DFF' : '#757993' }) })),
    h('button', {
      class: 'icon-btn', 'aria-label': 'Sort by balance',
      onclick: () => setAccountSort('balance')
    }, h('span', { html: iconMarkup(balanceIcon, { size: 16, color: balanceActive ? '#4D4DFF' : '#757993' }) }))
  ]);
}

function editableField(s, w, field, className) {
  const editing = s.homeEdit.accountId === w.id && s.homeEdit.field === field;
  if (editing) {
    return h('div', { class: 'field-row' }, [
      h('input', {
        value: s.homeEdit.draft,
        oninput: (e) => setHomeEditDraft(e.target.value),
        onkeydown: (e) => { if (e.key === 'Enter') confirmHomeEdit(); if (e.key === 'Escape') cancelHomeEdit(); }
      }),
      h('button', { class: 'btn btn-primary btn-sm', onclick: confirmHomeEdit }, 'Save'),
      h('button', { class: 'btn btn-secondary btn-sm', onclick: cancelHomeEdit }, 'Cancel')
    ]);
  }
  const value = field === 'name' ? w.name : (w.owner || '—');
  return h('span', { class: className, onclick: () => startHomeEdit(w.id, field) }, value);
}

// The explorer-link icon needs an already-derived address (see
// ensureHomeAddresses/main.js, populated once at boot) — until it lands,
// the icon renders inert (href="#", click prevented) rather than not at
// all, so the row layout doesn't shift once the address resolves.
function balanceExplorerLink(s, w, ccy) {
  const address = s.homeAddresses[w.id + '|' + ccy];
  const chain = CONFIG.NET[ccy] || '';
  return h('a', {
    class: 'icon-btn-plain', 'aria-label': `${ccy} address on ${chain}`, title: chain,
    href: address ? `https://explorer.tynti.app/address/${address}` : '#',
    target: address ? '_blank' : undefined, rel: address ? 'noopener noreferrer' : undefined,
    onclick: (e) => { e.stopPropagation(); if (!address) e.preventDefault(); }
  }, h('span', { html: iconMarkup('link', { size: 13, color: '#757993' }) }));
}

function accountCard(s, w) {
  const balanceRows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', { class: 'balance-row balance-row-clickable', onclick: () => goTransfer(w.id, ccy) }, [
      h('span', {}, `${ccy}  ${fmtNum(amt)}`),
      h('span', { class: 'flex-gap-sm items-center' }, [
        h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt))),
        balanceExplorerLink(s, w, ccy)
      ])
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      editableField(s, w, 'name', 'name'),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    editableField(s, w, 'owner', 'owner'),
    h('div', { class: 'balances' }, balanceRows)
  ]);
}

function addAccountTile() {
  return h('div', { class: 'account-card account-card-add', onclick: openOpenAccount }, [
    h('span', { html: iconMarkup('circlePlus', { size: 28, color: '#4D4DFF' }) })
  ]);
}

export function renderHome() {
  const s = getState();

  const sortedWallets = ACCOUNT_SORTS[s.accountSort]
    ? [...s.wallets].sort(ACCOUNT_SORTS[s.accountSort])
    : s.wallets;

  const accountCards = sortedWallets.map((w) => accountCard(s, w));

  const recentRows = s.transactions.slice(0, 4).map((t) => {
    const amountFmt = (t.amount < 0 ? '-' : '+') + fmtReporting(Math.abs(toReporting(t.ccy, t.amount)));
    return h('div', { class: 'tx-row clickable', onclick: () => goActivityFromHome(t.id) }, [
      h('div', {}, [
        h('div', { class: 'desc' }, t.desc),
        h('div', { class: 'meta' }, t.date)
      ]),
      h('span', { class: 'amount ' + (t.amount > 0 ? 'positive' : 'negative') }, amountFmt)
    ]);
  });

  return h('div', { class: 'screen' }, [
    h('div', {}, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'balance-label' }, `Total balance (${s.reportingCurrency})`),
        h('button', { class: 'icon-btn-plain', 'aria-label': 'What is this?', onclick: toggleBalanceInfo },
          h('span', { html: iconMarkup('circleQuestionMark', { size: 13, color: '#757993' }) }))
      ]),
      s.showBalanceInfo ? h('div', { class: 'text-sm text-muted mb-sm' },
        `This is the combined value of every account below, converted into your reporting currency (${s.reportingCurrency}) ` +
        'so accounts holding different stablecoins can be added together. Change the reporting currency in Settings.'
      ) : null,
      h('div', { class: 'balance-hero' }, fmtReporting(totalReportingAll())),
      h('div', { class: 'flex-between' }, [
        h('div', { class: 'section-label' }, 'Accounts'),
        s.wallets.length > 1 ? sortIcons(s) : null
      ]),
      h('div', { class: 'stack' }, [...accountCards, addAccountTile()]),
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'section-label' }, 'Recent activity'),
        h('button', { class: 'icon-btn-plain', 'aria-label': 'What is this?', onclick: toggleActivityInfo },
          h('span', { html: iconMarkup('circleQuestionMark', { size: 13, color: '#757993' }) }))
      ]),
      s.showActivityInfo ? h('div', { class: 'text-sm text-muted mb-sm' },
        'These are the actual transactions recorded on the blockchain for the accounts above — not just entries in an app database.'
      ) : null,
      s.transactions.length
        ? h('div', { class: 'tx-list' }, recentRows)
        : h('div', { class: 'empty-state' }, 'No activity yet.')
    ])
  ]);
}
